import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetClose } from "@/components/ui/sheet";
import { BrowserFrame } from "@/components/landing/BrowserFrame";
import { HeroPhoneMockup } from "@/components/landing/HeroPhoneMockup";
import { Section, Reveal, Eyebrow } from "@/components/landing/Section";
import { DemoRequestDialog } from "@/components/landing/DemoRequestDialog";
import {
    List, X, ArrowRight, Buildings, Wallet, Wrench, ShieldCheck, Scales, Storefront, Bell,
    CheckCircle, ArrowUpRight, ChatCircleDots, ClipboardText, Vault,
} from "@phosphor-icons/react";

const NAV_LINKS = [
    { label: "Platform", href: "#platform" },
    { label: "Solutions", href: "#solutions" },
    { label: "Governance", href: "#governance" },
    { label: "Why Maintayn", href: "#why-maintayn" },
];

const MODULES = [
    { icon: Buildings, title: "Community", copy: "Flats, residents, vehicles, amenities and the day-to-day information that keeps a community connected." },
    { icon: Wallet, title: "Finance", copy: "Invoices, collections, payments, receipts and outstanding-amount visibility for admins and residents alike." },
    { icon: Wrench, title: "Operations", copy: "Complaints, work orders and staff/vendor coordination — every issue tracked from report to resolution." },
    { icon: ShieldCheck, title: "Gate & Security", copy: "Visitor and vehicle workflows with clear approval trails at the gate." },
    { icon: Scales, title: "Governance", copy: "Notices, elections, polls, resolutions and community documents — decisions made transparently." },
    { icon: Storefront, title: "Community Commerce", copy: "Connecting residents with the local businesses and services around them.", soon: true },
];

function scrollTo(href) {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function NavAnchor({ href, children, dark, onClick }) {
    return (
        <button
            onClick={() => { scrollTo(href); onClick?.(); }}
            className={`text-sm font-medium transition-colors ${dark ? "text-white/80 hover:text-white" : "text-brand-inkSoft hover:text-brand-ink"}`}
        >
            {children}
        </button>
    );
}

function Header({ onBookDemo }) {
    const [mobileOpen, setMobileOpen] = useState(false);
    return (
        <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-brand-line" data-testid="landing-header">
            <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
                <Link to="/" className="flex items-center" data-testid="landing-logo">
                    <img
                        src="/maintayn-logo-onlight.png"
                        alt="maintayn"
                        draggable={false}
                        className="h-auto w-[112px] sm:w-[130px] lg:w-[140px] object-contain select-none"
                    />
                </Link>
                <nav className="hidden lg:flex items-center gap-8">
                    {NAV_LINKS.map((l) => <NavAnchor key={l.href} href={l.href}>{l.label}</NavAnchor>)}
                </nav>
                <div className="hidden lg:flex items-center gap-3">
                    <Link to="/login" data-testid="landing-signin-link" className="text-sm font-medium text-brand-inkSoft hover:text-brand-ink px-2">Sign In</Link>
                    <Button onClick={onBookDemo} data-testid="landing-nav-book-demo" className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white px-6">
                        Book a Demo
                    </Button>
                </div>
                <button className="lg:hidden p-2 text-brand-ink" onClick={() => setMobileOpen(true)} data-testid="landing-mobile-menu-toggle">
                    <List size={24} />
                </button>
            </div>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetContent side="right" className="w-[85%] sm:w-80 bg-white p-0" data-testid="landing-mobile-drawer">
                    <div className="flex items-center justify-between h-20 px-6 border-b border-brand-line">
                        <img
                            src="/maintayn-logo-onlight.png"
                            alt="maintayn"
                            draggable={false}
                            className="h-auto w-[112px] object-contain select-none"
                        />
                        <SheetClose asChild>
                            <button data-testid="landing-mobile-menu-close"><X size={22} /></button>
                        </SheetClose>
                    </div>
                    <nav className="flex flex-col gap-1 p-6">
                        {NAV_LINKS.map((l) => (
                            <button key={l.href} onClick={() => { scrollTo(l.href); setMobileOpen(false); }}
                                className="text-left py-3 text-base font-medium text-brand-ink border-b border-brand-line/60">
                                {l.label}
                            </button>
                        ))}
                        <Link to="/login" onClick={() => setMobileOpen(false)} className="py-3 text-base font-medium text-brand-ink border-b border-brand-line/60">Sign In</Link>
                        <Button onClick={() => { setMobileOpen(false); onBookDemo(); }} className="mt-6 w-full rounded-full bg-brand-action hover:bg-brand-actionHover text-white py-6" data-testid="landing-mobile-book-demo">
                            Book a Demo
                        </Button>
                    </nav>
                </SheetContent>
            </Sheet>
        </header>
    );
}

function Hero({ onBookDemo }) {
    return (
        <section className="bg-brand-bg pt-16 pb-24 md:pt-24 md:pb-32 px-6 lg:px-12 overflow-hidden" data-testid="landing-hero">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-16 items-center">
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <Eyebrow>Community Management Platform</Eyebrow>
                    <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-ink leading-[1.08]">
                        One platform to run your entire community.
                    </h1>
                    <p className="text-brand-inkSoft text-lg mt-6 leading-relaxed max-w-lg">
                        Maintayn brings community management, finance, operations, governance, gate management
                        and resident engagement together — in one simple, connected platform.
                    </p>
                    <div className="flex flex-wrap gap-3 mt-9">
                        <Button data-testid="hero-book-demo-btn" onClick={onBookDemo} size="lg" className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white px-8 py-6 text-base font-semibold">
                            Book a Demo <ArrowRight size={18} className="ml-2" />
                        </Button>
                        <Button data-testid="hero-explore-btn" onClick={() => scrollTo("#platform")} size="lg" variant="outline" className="rounded-full border-brand-ink/20 text-brand-ink px-8 py-6 text-base font-semibold">
                            Explore Maintayn
                        </Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-10 text-xs text-brand-inkSoft">
                        {["Community", "Finance", "Operations", "Governance"].map((t) => (
                            <span key={t} className="flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-brand-action" />{t}</span>
                        ))}
                    </div>
                </motion.div>

                <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative">
                    <BrowserFrame src="/marketing/admin-dashboard.jpg" alt="Maintayn admin command-center dashboard" className="relative z-10" />
                    <div className="hidden md:block absolute -bottom-8 -right-2 md:-right-2 lg:-right-12 z-20 rotate-[3deg]">
                        <HeroPhoneMockup width={190} />
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

function PlatformModules() {
    return (
        <Section id="platform" bg="charcoal">
            <Reveal className="max-w-2xl mb-14">
                <Eyebrow dark>The Platform</Eyebrow>
                <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">Everything your community needs. One connected platform.</h2>
                <p className="text-white/70 mt-4 text-lg">Maintayn connects the major parts of community management instead of forcing committees and managers to juggle disconnected tools.</p>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {MODULES.map((m, i) => (
                    <Reveal key={m.title} delay={i * 0.06}>
                        <div className="bg-white/5 border border-white/10 rounded-xl p-6 h-full hover:bg-white/10 hover:-translate-y-1 transition-all duration-300" data-testid={`module-card-${m.title.toLowerCase().replace(/\s+/g, "-")}`}>
                            <div className="w-11 h-11 rounded-lg bg-brand-action/15 flex items-center justify-center mb-4">
                                <m.icon size={22} weight="duotone" className="text-brand-action" />
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-heading font-semibold text-lg">{m.title}</h3>
                                {m.soon && <span className="text-[9px] font-semibold uppercase tracking-widest text-[#FF9A5C] border border-[#FF9A5C]/30 rounded-full px-2 py-0.5">Coming Soon</span>}
                            </div>
                            <p className="text-white/60 text-sm leading-relaxed">{m.copy}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </Section>
    );
}

function BuiltForAdmins() {
    const points = ["Community management across flats, residents and vehicles", "Finance visibility — invoices, collections and outstanding amounts", "Operations tracking for complaints, work orders and staff/vendors", "Governance tools for notices, voting and documents", "Gate approval workflows", "A connected Notification Center"];
    return (
        <Section id="solutions" bg="white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <Reveal>
                    <Eyebrow>Built for Admins &amp; Committees</Eyebrow>
                    <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight text-brand-ink">Run your community from one command center.</h2>
                    <p className="text-brand-inkSoft mt-4 text-lg leading-relaxed">Administrators and committee members manage the entire community from a single, connected dashboard — no more switching between spreadsheets, chat groups and paper registers.</p>
                    <ul className="mt-8 space-y-3">
                        {points.map((p) => (
                            <li key={p} className="flex items-start gap-3 text-brand-ink">
                                <CheckCircle size={18} weight="fill" className="text-brand-action mt-0.5 shrink-0" />
                                <span>{p}</span>
                            </li>
                        ))}
                    </ul>
                </Reveal>
                <Reveal delay={0.1}>
                    <div className="grid gap-5">
                        <BrowserFrame src="/marketing/admin-dashboard.jpg" alt="Admin dashboard overview" />
                        <div className="grid grid-cols-2 gap-5">
                            <BrowserFrame src="/marketing/admin-finance.jpg" alt="Admin finance view" height={160} />
                            <BrowserFrame src="/marketing/admin-operations.jpg" alt="Admin operations view" height={160} />
                        </div>
                    </div>
                </Reveal>
            </div>
        </Section>
    );
}

function BuiltForResidents() {
    const points = ["View your dues and payment history", "Raise complaints and track resolution", "Book shared amenities", "Manage household vehicles", "Read notices and participate in voting", "Get every update in one Notification Center"];
    return (
        <Section bg="soft">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <Reveal className="order-2 lg:order-1 flex justify-center">
                    <HeroPhoneMockup width={240} />
                </Reveal>
                <Reveal delay={0.1} className="order-1 lg:order-2">
                    <Eyebrow>Built for Residents</Eyebrow>
                    <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight text-brand-ink">A simpler experience for every resident.</h2>
                    <p className="text-brand-inkSoft mt-4 text-lg leading-relaxed">A calmer, lighter experience — the same connected platform, tuned for everyday life in the community.</p>
                    <ul className="mt-8 space-y-3">
                        {points.map((p) => (
                            <li key={p} className="flex items-start gap-3 text-brand-ink">
                                <CheckCircle size={18} weight="fill" className="text-brand-action mt-0.5 shrink-0" />
                                <span>{p}</span>
                            </li>
                        ))}
                    </ul>
                </Reveal>
            </div>
        </Section>
    );
}

function SplitFeature({ id, bg, eyebrow, title, copy, points, image, imageAlt, reverse, dark }) {
    return (
        <Section id={id} bg={bg}>
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <Reveal>
                    <Eyebrow dark={dark}>{eyebrow}</Eyebrow>
                    <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">{title}</h2>
                    <p className={`mt-4 text-lg leading-relaxed ${dark ? "text-white/70" : "text-brand-inkSoft"}`}>{copy}</p>
                    {points && (
                        <ul className="mt-7 space-y-3">
                            {points.map((p) => (
                                <li key={p} className="flex items-start gap-3">
                                    <CheckCircle size={18} weight="fill" className="text-brand-action mt-0.5 shrink-0" />
                                    <span>{p}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </Reveal>
                <Reveal delay={0.1}>
                    <BrowserFrame src={image} alt={imageAlt} />
                </Reveal>
            </div>
        </Section>
    );
}

function ComingSoonFeature() {
    return (
        <Section bg="white">
            <Reveal className="max-w-3xl mx-auto text-center">
                <Eyebrow>Community Commerce</Eyebrow>
                <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight text-brand-ink">Connect residents with the businesses and services around them.</h2>
                <p className="text-brand-inkSoft mt-4 text-lg leading-relaxed">
                    Maintayn is expanding to bring local grocery, medical, dairy and neighbourhood services closer to residents —
                    strengthening the relationship between a community and its local ecosystem.
                </p>
                <span className="inline-flex items-center gap-2 mt-6 text-xs font-semibold uppercase tracking-widest text-brand-action border border-brand-action/30 rounded-full px-4 py-1.5">
                    Expanding platform area · Coming Soon
                </span>
            </Reveal>
        </Section>
    );
}

function WhyMaintayn() {
    const items = [
        { icon: Vault, title: "One Platform", copy: "Replace scattered spreadsheets, chat groups and registers with a single connected system." },
        { icon: ChatCircleDots, title: "Connected Experience", copy: "Admin and resident experiences work together in real time, not as two separate products." },
        { icon: ClipboardText, title: "Operational Visibility", copy: "Issues, payments and everyday activity become trackable instead of getting lost." },
        { icon: Bell, title: "Better Resident Experience", copy: "Residents get one place for finances, requests, notices, voting and notifications." },
        { icon: Scales, title: "Structured Governance", copy: "Notices, elections, polls and resolutions bring transparency to community decisions." },
        { icon: ArrowUpRight, title: "Scalable Foundation", copy: "Built as a platform designed to grow with communities anywhere, not tied to one geography." },
    ];
    return (
        <Section id="why-maintayn" bg="soft">
            <Reveal className="max-w-2xl mb-14">
                <Eyebrow>Why Maintayn</Eyebrow>
                <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight text-brand-ink">A platform built differently.</h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {items.map((it, i) => (
                    <Reveal key={it.title} delay={i * 0.05}>
                        <div className="bg-white border border-brand-line rounded-xl p-6 h-full hover:shadow-md transition-shadow duration-300" data-testid={`why-card-${i}`}>
                            <it.icon size={26} weight="duotone" className="text-brand-action mb-4" />
                            <h3 className="font-heading font-semibold text-lg text-brand-ink mb-2">{it.title}</h3>
                            <p className="text-brand-inkSoft text-sm leading-relaxed">{it.copy}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </Section>
    );
}

function SocialProof() {
    return (
        <Section bg="white" className="!py-14">
            <Reveal className="text-center">
                <p className="text-xs font-semibold uppercase tracking-overline text-brand-inkSoft mb-6">Built for communities getting started with Maintayn</p>
                <div className="flex flex-wrap justify-center gap-4">
                    {["Residential Communities", "Apartment Complexes", "Housing Societies", "Facility Management Teams", "Resident Associations"].map((t) => (
                        <span key={t} className="text-sm font-medium text-brand-inkSoft border border-brand-line rounded-full px-5 py-2" data-testid={`social-proof-chip-${t.toLowerCase().replace(/\s+/g,'-')}`}>{t}</span>
                    ))}
                </div>
            </Reveal>
        </Section>
    );
}

function FinalCTA({ onBookDemo }) {
    return (
        <Section bg="orange" className="text-center">
            <Reveal>
                <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight max-w-2xl mx-auto">Ready to make your community simpler to manage?</h2>
                <p className="text-white/85 mt-4 text-lg max-w-xl mx-auto">See how Maintayn can bring your community's people, processes and operations together.</p>
                <div className="flex flex-wrap justify-center gap-3 mt-9">
                    <Button data-testid="final-cta-book-demo" onClick={onBookDemo} size="lg" className="rounded-full bg-white text-brand-action hover:bg-white/90 px-8 py-6 text-base font-semibold">
                        Book a Demo
                    </Button>
                    <Button data-testid="final-cta-talk" onClick={onBookDemo} size="lg" variant="outline" className="rounded-full border-white/40 text-white hover:bg-white/10 px-8 py-6 text-base font-semibold">
                        Talk to Maintayn
                    </Button>
                </div>
            </Reveal>
        </Section>
    );
}

function Footer() {
    return (
        <footer className="bg-brand-ink text-white/70 pt-16 pb-8 px-6 lg:px-12" data-testid="landing-footer">
            <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
                <div>
                    <div className="flex items-center gap-2 mb-4">
                        <LogoMark size={26} variant="onDark" />
                        <span className="font-heading font-bold text-xl text-white">maintayn</span>
                    </div>
                    <p className="text-sm leading-relaxed max-w-xs">A modern community management platform bringing community, finance, operations and governance together.</p>
                </div>
                <div>
                    <p className="text-xs font-semibold uppercase tracking-overline text-white/40 mb-4">Product</p>
                    <ul className="space-y-2.5 text-sm">
                        {["Community", "Finance", "Operations", "Gate", "Governance", "Community Commerce"].map((l) => (
                            <li key={l}><button onClick={() => scrollTo("#platform")} className="hover:text-white transition-colors">{l}</button></li>
                        ))}
                    </ul>
                </div>
                <div>
                    <p className="text-xs font-semibold uppercase tracking-overline text-white/40 mb-4">Company</p>
                    <ul className="space-y-2.5 text-sm">
                        <li><button onClick={() => scrollTo("#why-maintayn")} className="hover:text-white transition-colors">About</button></li>
                        <li><Link to="/login" className="hover:text-white transition-colors">Sign In</Link></li>
                    </ul>
                </div>
                <div>
                    <p className="text-xs font-semibold uppercase tracking-overline text-white/40 mb-4">Legal</p>
                    <ul className="space-y-2.5 text-sm">
                        <li><Link to="/privacy-policy" data-testid="footer-privacy-policy-link" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                        <li><Link to="/delete-account" data-testid="footer-delete-account-link" className="hover:text-white transition-colors">Delete Account</Link></li>
                    </ul>
                </div>
            </div>
            <div className="max-w-7xl mx-auto border-t border-white/10 mt-12 pt-6 text-xs text-white/40 flex flex-wrap items-center justify-between gap-2">
                <span>© {new Date().getFullYear()} maintayn. All rights reserved.</span>
                <span className="flex gap-4">
                    <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
                    <Link to="/delete-account" className="hover:text-white transition-colors">Delete Account</Link>
                </span>
            </div>
        </footer>
    );
}

export default function Landing() {
    const [demoOpen, setDemoOpen] = useState(false);
    const openDemo = () => setDemoOpen(true);
    return (
        <div className="bg-white" data-testid="landing-page">
            <Header onBookDemo={openDemo} />
            <Hero onBookDemo={openDemo} />
            <PlatformModules />
            <BuiltForAdmins />
            <BuiltForResidents />
            <SplitFeature bg="charcoal" dark eyebrow="Finance" title="Know where your community stands financially."
                copy="A clear, real-time view of invoices, collections and outstanding amounts — for admins overseeing the books and residents tracking their own dues."
                points={["Invoice management and generation", "Collections and payment tracking", "Outstanding-amount and defaulter visibility", "Digital receipts for every payment", "A dedicated resident finance view"]}
                image="/marketing/admin-finance.jpg" imageAlt="Maintayn finance dashboard" />
            <SplitFeature id="operations" bg="white" eyebrow="Operations" title="Turn everyday issues into trackable operations."
                copy="Every complaint moves through a clear lifecycle — Complaint → Assignment → Work Order → Resolution — so nothing gets lost."
                points={["Resident complaint creation", "Staff and vendor assignment", "Work orders with status tracking", "Overdue visibility for admins", "Notifications at every step"]}
                image="/marketing/admin-operations.jpg" imageAlt="Maintayn operations dashboard" reverse />
            <SplitFeature id="governance" bg="soft" eyebrow="Governance" title="Make community decisions transparent."
                copy="Notices, elections, polls and resolutions — with clear audience targeting, participation tracking and results, all in one place."
                points={["Notices: draft, publish, schedule, archive", "Elections, polls and resolutions with results", "Audience targeting across the community", "Community documents and templates"]}
                image="/marketing/admin-voting.jpg" imageAlt="Maintayn governance and voting dashboard" />
            <SplitFeature bg="deepCharcoal" dark eyebrow="Gate &amp; Security" title="Clear approval workflows at the gate."
                copy="Visitor and vehicle entries move through a structured approval flow, giving committees a clear record of who's coming and going."
                points={["Visitor and vehicle approval workflows", "Resident-submitted vehicle requests", "Approval/rejection with a clear audit trail"]}
                image="/marketing/admin-gate.jpg" imageAlt="Maintayn gate and vehicle approval" reverse />
            <ComingSoonFeature />
            <SplitFeature bg="white" eyebrow="Notification Center" title="Every update, in one connected inbox."
                copy="From vehicle approvals to complaint updates, notices and voting — residents get every important update from a single, centralized Notification Center."
                points={["Vehicle approval and rejection alerts", "Complaint lifecycle updates", "Notice and governance updates", "One inbox across the whole platform"]}
                image="/marketing/notification-center.jpg" imageAlt="Maintayn notification center" reverse />
            <WhyMaintayn />
            <SocialProof />
            <FinalCTA onBookDemo={openDemo} />
            <Footer />
            <DemoRequestDialog open={demoOpen} onOpenChange={setDemoOpen} />
        </div>
    );
}
